import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SmartLinkServices } from "@/services/bedrock";
import { SmartLink, SmartLinkSearchQuery } from "@/services/bedrock/dtos";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { toast } from "sonner";

export interface UseSmartLinkSearchOptions extends SmartLinkSearchQuery {
  searchTerm?: string;
  page?: number;
  perPage?: number;
  enabled?: boolean;
}

export function useSmartLinkSearch(options: UseSmartLinkSearchOptions = {}) {
  const { coachId } = useSocialCoachSession();
  const queryClient = useQueryClient();
  const { search, searchTerm, isActive, extraFields, page = 1, perPage = 25, enabled = true } = options;
  const term = searchTerm ?? search;

  const queryKey = ["smart-links", coachId, term, isActive, extraFields, page, perPage];

  const query = useQuery({
    queryKey,
    queryFn: () =>
      SmartLinkServices.getSmartLinks({
        search: term,
        isActive,
        extraFields,
        page,
        perPage,
      }),
    enabled: !!coachId && enabled,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<SmartLink>) => SmartLinkServices.createSmartLink({ ...data, coachId: coachId! }),
    onSuccess: newLink => {
      toast.success("Smart Link created successfully");
      queryClient.invalidateQueries({ queryKey: ["smart-links"] });
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to create Smart Link");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SmartLink> }) => SmartLinkServices.updateSmartLink(id, data),
    onSuccess: updatedLink => {
      toast.success(
        updatedLink.isActive === false ? "Smart Link archived successfully" : "Smart Link updated successfully"
      );
      queryClient.invalidateQueries({ queryKey: ["smart-links"] });
    },
    onError: error => {
      console.error(error);
      toast.error("Failed to update Smart Link");
    },
  });

  return {
    ...query,
    createSmartLink: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateSmartLink: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
